var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
/**
 * CZNA-473 — the source JSON behind an ontology detail.
 *
 * Everything else on the ontology screen is a *rendering*: Czech labels instead
 * of identifiers, translated enums, coloured badges, a derived coverage tally.
 * That is the right default for the reader, but it leaves no way to check what
 * the screen was rendered FROM. Since CZNA-448 there is exactly one source —
 * `semantic-catalog.json` — and the browser already holds it verbatim: the
 * controller returns `catalogProvider.catalog()` untouched, so
 * `data.classes[name]` IS the file's block, not a DTO built from it. This panel
 * prints that block back with `JSON.stringify(value, null, 2)`, which matches the
 * file's own two-space indentation and (like Jackson) leaves Czech diacritics
 * unescaped — so a slice can be compared against the file by eye.
 *
 * <p><b>Every block carries its real address.</b> A slice labelled
 * `classDerivation[3]` can be looked up in the file; a synthetic "derivation rules
 * for Osoba" array could not, and would quietly become a fourth artifact to keep
 * in sync. That is why this takes a list of `(path, value)` pairs rather than one
 * merged object: a class detail renders its own block *and* the derivation rules
 * that live at the top level, and both must stay addressable.
 *
 * <p>Closed by default, and last in the detail — this is the technical floor
 * under a screen the customer reads, not its headline.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Accordion, Button } from '@folio/stripes/components';
import HelpTooltip from '../components/HelpTooltip';
/** The file the slices come from — named on screen so the address is complete. */
export var CATALOG_FILE = 'semantic-catalog.json';
var MONO_FONT = "'SF Mono', 'Cascadia Code', 'Consolas', monospace";
var preStyle = {
    fontFamily: MONO_FONT,
    fontSize: '0.78rem',
    lineHeight: 1.5,
    background: '#f8fafc',
    border: '1px solid #cbd5e1',
    borderRadius: '6px',
    padding: '12px',
    margin: '4px 0 12px',
    maxHeight: '420px',
    overflow: 'auto',
    whiteSpace: 'pre',
};
var pathStyle = {
    fontFamily: MONO_FONT,
    fontSize: '0.78rem',
    color: '#475569',
};
var OntologySourceJson = function (_a) {
    var slices = _a.slices, version = _a.version;
    var _b = useState(false), copied = _b[0], setCopied = _b[1];
    var copiedTimerRef = useRef(undefined);
    // Clear the "Copied" reset timer if the detail unmounts before it fires.
    useEffect(function () { return function () { return clearTimeout(copiedTimerRef.current); }; }, []);
    /**
     * Rendered once per slice, and joined for the clipboard. Copying gets the
     * address comments too: a bare block pasted into a ticket is unidentifiable,
     * and identifying it is the whole reason this panel exists.
     */
    var rendered = useMemo(function () { return slices.map(function (slice) { return ({
        path: slice.path,
        text: JSON.stringify(slice.value, null, 2),
    }); }); }, [slices]);
    var clipboardText = useMemo(function () { return rendered
        .map(function (_a) {
        var path = _a.path, text = _a.text;
        return "// ".concat(CATALOG_FILE, " \u2192 ").concat(path, "\n").concat(text);
    })
        .join('\n\n'); }, [rendered]);
    var handleCopy = useCallback(function () { return __awaiter(void 0, void 0, void 0, function () {
        var _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    _b.trys.push([0, 2, , 3]);
                    return [4 /*yield*/, navigator.clipboard.writeText(clipboardText)];
                case 1:
                    _b.sent();
                    setCopied(true);
                    clearTimeout(copiedTimerRef.current);
                    copiedTimerRef.current = setTimeout(function () { return setCopied(false); }, 2000);
                    return [3 /*break*/, 3];
                case 2:
                    _a = _b.sent();
                    return [3 /*break*/, 3];
                case 3: return [2 /*return*/];
            }
        });
    }); }, [clipboardText]);
    if (!rendered.length)
        return null;
    return (React.createElement(Accordion, { id: "ontology-source-json", label: React.createElement(FormattedMessage, { id: "ui-czna-authorities.semanticCatalog.sourceJson.label" }), closedByDefault: true },
        React.createElement("div", { style: { display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' } },
            React.createElement("span", { style: pathStyle, "data-testid": "ontology-source-json-file" },
                CATALOG_FILE,
                version ? " (v".concat(version, ")") : ''),
            React.createElement(HelpTooltip, { messageId: "ui-czna-authorities.semanticCatalog.help.sourceJson", testKey: "sourceJson", idPrefix: "ontology-help" }),
            React.createElement(Button, { "data-testid": "ontology-source-json-copy", buttonStyle: "default", onClick: handleCopy, marginBottom0: true },
                React.createElement(FormattedMessage, { id: copied
                        ? 'ui-czna-authorities.semanticCatalog.sourceJson.copied'
                        : 'ui-czna-authorities.semanticCatalog.sourceJson.copy' }))),
        rendered.map(function (_a) {
            var path = _a.path, text = _a.text;
            return (React.createElement("div", { key: path },
                React.createElement("div", { style: pathStyle, "data-testid": "ontology-source-json-path" }, path),
                React.createElement("pre", { "data-testid": "ontology-source-json", style: preStyle }, text)));
        })));
};
export default OntologySourceJson;
